/**
 * Province border rendering — v0M2.4a.
 *
 * Scans the provinceMap for cell-to-cell transitions and draws a dark
 * stroke along every edge where two neighbouring cells belong to
 * different provinces. Baked once per zoom step (same cadence as the
 * terrain canvas) — borders are static until the political layer
 * mutates, and re-scanning 64k cells per frame would be wasteful.
 */

import type { Camera } from './camera';
import type { LoadedMap } from './map-loader';
import { pxPerCell } from './camera';

const BORDER_STROKE = 'rgba(24, 20, 12, 0.32)';

/**
 * Bake province borders into an offscreen canvas the same size as the
 * baked terrain canvas. The renderer composites this over the terrain
 * layer and under the entity layer.
 */
export function bakeProvinceBorders(map: LoadedMap, camera: Camera): HTMLCanvasElement {
  const z = pxPerCell(camera);
  const off = document.createElement('canvas');
  off.width = Math.ceil(map.width * z);
  off.height = Math.ceil(map.height * z);
  const ctx = off.getContext('2d');
  if (!ctx) throw new Error('bakeProvinceBorders: offscreen 2D context unavailable');

  ctx.strokeStyle = BORDER_STROKE;
  ctx.lineWidth = Math.max(1, z * 0.15);
  ctx.lineCap = 'square';

  // Draw the east edge of every cell where east neighbour differs.
  ctx.beginPath();
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width - 1; x++) {
      const here = map.provinceMap[y * map.width + x]!;
      const east = map.provinceMap[y * map.width + (x + 1)]!;
      if (here !== east) {
        const px = (x + 1) * z;
        ctx.moveTo(px, y * z);
        ctx.lineTo(px, (y + 1) * z);
      }
    }
  }
  // Draw the south edge of every cell where south neighbour differs.
  for (let y = 0; y < map.height - 1; y++) {
    for (let x = 0; x < map.width; x++) {
      const here = map.provinceMap[y * map.width + x]!;
      const south = map.provinceMap[(y + 1) * map.width + x]!;
      if (here !== south) {
        const py = (y + 1) * z;
        ctx.moveTo(x * z, py);
        ctx.lineTo((x + 1) * z, py);
      }
    }
  }
  ctx.stroke();
  return off;
}
